<template>
  <div v-if="hasVehicles">
    <ModalToggleVehicleStatus
      v-if="pendingToggle"
      :value="true"
      :vehicle="pendingToggle.vehicle"
      :action="pendingToggle.action"
      @input="pendingToggle = null"
      @confirmed="onStatusConfirmed"
    />
    <table class="table table-borderless table-border table-align table-h51">
      <thead>
      <tr>
        <th>
          <BkpsCheckBox @input="$emit('changeCheckAllRow', $event)" :value="checkAllRow"/>
        </th>
        <th>Placa</th>
        <th>Renavam</th>
        <th>CPF/CNPJ</th>
        <th>Código da Frota</th>
        <th>Criado por</th>
        <th>Atualizado por</th>
        <th>Criado em</th>
        <th>Atualizado em</th>
        <th>Status</th>
        <th>CRLV <CrlvHint/></th>
        <th></th>
      </tr>
      </thead>
      <tbody>
      <tr v-for="vehicle in vehicles" :key="vehicle.id">
        <td>
          <BkpsCheckBox v-model="vehicle.checked" @input="$emit('changeCheckRow', $event)"/>
        </td>
        <td>{{ vehicle.plate }}</td>
        <td>{{ vehicle.renavam }}</td>
        <td>{{ vehicle.cpfCnpj | cpfCnpj }}</td>
        <td>{{ vehicle.fleetCode || '-' }}</td>
        <td>{{ vehicle.createdBy }}</td>
        <td>{{ vehicle.updatedBy || '-' }}</td>
        <td>{{ vehicle.createdAt | datetime }}</td>
        <td>{{ vehicle.updatedAt | datetime }}</td>
        <td>
          <VehicleStatusToggle :vehicle="vehicle" @request-toggle="pendingToggle = $event"/>
        </td>
        <td class="text-nowrap">
          <CrlvCaptureToggle
              v-if="$withPolicy('application', 'vehicles/insert_or_update')"
              :vehicle="vehicle"
              @request-toggle="$emit('requestCrlvToggle', $event)"
          />
          <span
              class="badge crlv-badge-link"
              :class="crlvBadge(vehicle).klass"
              :title="crlvBadge(vehicle).title || 'Abrir o CRLV do veículo'"
              @click.stop="$emit('showVehicle', vehicle)"
          >
            {{ crlvBadge(vehicle).label }}
          </span>
          <small v-if="crlvBadge(vehicle).countdown" class="crlv-countdown" :class="crlvCountdownClass(vehicle)">
            <clock-icon size="0.8x" class="crlv-clock" :class="crlvClockClass(vehicle)"/>
            {{ crlvBadge(vehicle).countdown }}
          </small>
          <small v-if="crlvBadge(vehicle).failure" class="crlv-failure" :class="{ 'crlv-failure-user': crlvBadge(vehicle).userError }" :title="crlvBadge(vehicle).title">
            <alert-triangle-icon size="0.8x" class="crlv-failure-icon"/>
            {{ crlvBadge(vehicle).failure }}
          </small>
        </td>
        <td class="text-right">
          <button @click="$emit('showVehicle', vehicle)" class="btn-icon btn-outline">
            <edit-icon size="1.5x"/>
          </button>

          <button
              v-if="$withPolicy('application', 'vehicles/delete')"
              @click="$emit('sendDeleteVehicle', vehicle)"
              class="btn-icon btn-outline-red ml-2 mr-3"
          >
            <trash-2-icon size="1.5x"/>
          </button>
        </td>
      </tr>
      </tbody>
    </table>
  </div>
  <div v-else>
    <p class="mt-4">
      {{ getUser.role === 'owner' ? 'Nenhum veículo cadastrado' : 'Nenhum veículo cadastrado ou você não possui acesso' }}
    </p>
  </div>

</template>

<script>

  import BkpsCheckBox from '@/components/shares/specialTag/BkpsCheckBox'
  import VehicleStatusToggle from '@/components/vehicles/VehicleStatusToggle'
  import ModalToggleVehicleStatus from '@/components/vehicles/ModalToggleVehicleStatus'
  import CrlvHint from '@/components/vehicles/CrlvHint'
  import CrlvCaptureToggle from '@/components/vehicles/CrlvCaptureToggle'
  import { crlvBadge } from '@/utils/crlvHelp'
  import { VEHICLE_STATUS } from '@/utils/vehicleStatus'
  import getUser from '@/mixins/GetUser.vue'
  import { AlertTriangleIcon, ClockIcon, Trash2Icon, EditIcon } from 'vue-feather-icons'

  export default {
    components: { AlertTriangleIcon, BkpsCheckBox, ClockIcon, CrlvCaptureToggle, CrlvHint, EditIcon, ModalToggleVehicleStatus, Trash2Icon, VehicleStatusToggle },
    mixins: [ getUser ],
    name: 'Table',
    props: {
      checkAllRow: { required: true, type: Boolean },
      vehicles: { required: true, type: Array }
    },
    data() {
      return { pendingToggle: null }
    },
    computed: {
      hasVehicles() {
        return this.vehicles.length > 0
      },
    },
    methods: {
      crlvBadge(vehicle) {
        return crlvBadge(vehicle.crlvSummary)
      },
      crlvDays(vehicle) {
        return vehicle.crlvSummary ? vehicle.crlvSummary.days_to_expire : null
      },
      crlvCountdownClass(vehicle) {
        const days = this.crlvDays(vehicle)
        if (days === null || days === undefined)
          return ''
        return days < 0 ? 'crlv-countdown-expired' : days <= 60 ? 'crlv-countdown-soon' : ''
      },
      crlvClockClass(vehicle) {
        const days = this.crlvDays(vehicle)
        if (days === null || days === undefined)
          return ''
        return days <= 60 ? 'crlv-clock-pulse' : 'crlv-clock-tick'
      },
      onStatusConfirmed() {
        const { vehicle, action } = this.pendingToggle
        vehicle.entityStatusId = action === 'disable' ? VEHICLE_STATUS.DISABLED : VEHICLE_STATUS.ENABLED
        this.pendingToggle = null
      }
    }
  }
</script>

<style scoped>
.badge {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
}

.badge-manual {
  background-color: #ede9fe;
  color: #5b21b6;
}

.badge-warning {
  background-color: #fef3c7;
  color: #b45309;
}

.crlv-badge-link {
  cursor: pointer;
}

.badge-user-error {
  background-color: #ffedd5;
  color: #c2410c;
}

.crlv-failure {
  display: flex;
  align-items: flex-start;
  gap: 4px;
  margin-top: 2px;
  max-width: 260px;
  font-size: 0.7rem;
  line-height: 1.2;
  color: var(--primary-text-color);
  opacity: 0.75;
}

.crlv-failure-user {
  color: #c2410c;
  opacity: 1;
}

.crlv-failure-icon {
  flex-shrink: 0;
  margin-top: 1px;
}

.crlv-countdown {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 2px;
  font-size: 0.7rem;
  color: var(--primary-text-color);
  opacity: 0.7;
}

.crlv-countdown-expired {
  color: #ed5565;
  opacity: 1;
}

.crlv-countdown-soon {
  color: #b45309;
  opacity: 1;
}

.crlv-clock {
  flex-shrink: 0;
  transform-origin: 50% 50%;
}

.crlv-clock-tick {
  animation: crlv-tick 2.4s ease-in-out infinite;
}

.crlv-clock-pulse {
  animation: crlv-pulse 1.4s ease-in-out infinite;
}

@keyframes crlv-tick {
  0%, 100% { transform: rotate(0deg); }
  25% { transform: rotate(-12deg); }
  75% { transform: rotate(12deg); }
}

@keyframes crlv-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.35; }
}
</style>
